import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const OWNER_TOKEN_KEY = "mateo_owner_token";

interface Reservation {
  id: string;
  excursion_slug: string;
  excursion_title: string;
  variant_label: string;
  variant_price: number;
  adults: number;
  children: number;
  infants: number;
  total_price: number;
  preferred_date: string | null;
  name: string;
  email: string;
  phone: string;
  message: string | null;
  status: string;
  created_at: string;
}

type PricingVariant = {
  id: string;
  label: string;
  price: number;
  child?: number;
  infant?: number;
};

interface ExcursionPricing {
  id: string;
  slug: string;
  title: string;
  variants: PricingVariant[];
  updated_at: string;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Potwierdzona", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Anulowana", cls: "bg-red-100 text-red-800" },
};

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"reservations" | "pricing">("reservations");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Reservations
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [filterStatus, setFilterStatus] = useState("all");

  // Pricing
  const [pricingData, setPricingData] = useState<ExcursionPricing[]>([]);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editVariants, setEditVariants] = useState<PricingVariant[]>([]);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingMsg, setPricingMsg] = useState("");

  const fetchReservations = useCallback(async () => {
    try {
      const password = sessionStorage.getItem("mateo_owner_pass");
      if (!password) {
        setError("Session expired — please log in again.");
        return;
      }

      const body: Record<string, string> = { password };
      if (filterStatus !== "all") body.status = filterStatus;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/get-reservations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let data: { success?: boolean; error?: string; data?: Reservation[] } | null = null;
      try { data = JSON.parse(responseText); } catch { /* not json */ }

      if (!response.ok || !data?.success) {
        setError(data?.error || `Server error (${response.status})`);
        setReservations([]);
        return;
      }

      setReservations(data?.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load reservations");
    }
  }, [filterStatus]);

  const fetchPricing = useCallback(async () => {
    try {
      const { data, error: rpcError } = await supabase
        .from("excursions_pricing")
        .select("*")
        .order("title");
      if (rpcError) throw rpcError;
      setPricingData((data as ExcursionPricing[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pricing");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([fetchReservations(), fetchPricing()]).finally(() =>
      setLoading(false),
    );
  }, [fetchReservations, fetchPricing]);

  async function updateReservationStatus(id: string, newStatus: string) {
    try {
      const password = sessionStorage.getItem("mateo_owner_pass");
      if (!password) {
        setError("Session expired — please log in again.");
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/update-reservation-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ password, id, status: newStatus }),
      });

      const responseText = await response.text();
      let data: Record<string, unknown> | null = null;
      try { data = JSON.parse(responseText); } catch { /* not json */ }

      if (!response.ok || !data?.success) {
        setError((data?.error as string) || `Server error (${response.status})`);
        return;
      }

      setReservations((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status");
    }
  }

  async function deleteReservation(id: string) {
    if (!window.confirm("Permanently delete this reservation? This cannot be undone.")) return;

    try {
      const password = sessionStorage.getItem("mateo_owner_pass");
      if (!password) {
        setError("Session expired — please log in again.");
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/delete-reservation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ password, id }),
      });

      const responseText = await response.text();
      let data: Record<string, unknown> | null = null;
      try { data = JSON.parse(responseText); } catch { /* not json */ }

      if (!response.ok || !data?.success) {
        setError((data?.error as string) || `Server error (${response.status})`);
        return;
      }

      setReservations((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete reservation");
    }
  }

  function startEditing(pricing: ExcursionPricing) {
    setEditingSlug(pricing.slug);
    setEditVariants(JSON.parse(JSON.stringify(pricing.variants)));
    setPricingMsg("");
  }

  function cancelEditing() {
    setEditingSlug(null);
    setEditVariants([]);
    setPricingMsg("");
  }

  function updateVariantPrice(index: number, price: number) {
    setEditVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], price };
      return next;
    });
  }

  function updateVariantChildPrice(index: number, child: number) {
    setEditVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], child };
      return next;
    });
  }

  function updateVariantInfantPrice(index: number, infant: number) {
    setEditVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], infant };
      return next;
    });
  }

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  async function savePricing(slug: string) {
    setSavingPricing(true);
    setPricingMsg("");
    try {
      const password = sessionStorage.getItem("mateo_owner_pass");
      if (!password) {
        setPricingMsg("Session expired — please log in again.");
        setSavingPricing(false);
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/update-excursion-pricing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ password, slug, variants: editVariants }),
      });

      const responseText = await response.text();
      let data: Record<string, unknown> | null = null;
      try { data = JSON.parse(responseText); } catch { /* not json */ }

      if (!response.ok || !data?.success) {
        setPricingMsg(
          (data?.error as string) || `Server error (${response.status})`
        );
      } else {
        setPricingMsg("Prices saved!");
        setEditingSlug(null);
        await fetchPricing();
      }
    } catch (err) {
      setPricingMsg(
        err instanceof Error ? err.message : "Failed to save pricing",
      );
    } finally {
      setSavingPricing(false);
    }
  }

  function handleSignOut() {
    localStorage.removeItem(OWNER_TOKEN_KEY);
    sessionStorage.removeItem("mateo_owner_pass");
    navigate("/owner/login", { replace: true });
  }

  const filteredReservations =
    filterStatus === "all"
      ? reservations
      : reservations.filter((r) => r.status === filterStatus);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top bar */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-heading text-lg font-semibold text-foreground-950">
              Mateo Teneryfa
            </span>
            <span className="hidden sm:inline text-xs text-foreground-500 uppercase tracking-[0.15em] bg-background-100 px-2 py-0.5 rounded-full">
              Panel Właściciela
            </span>
          </div>
          <button
            onClick={handleSignOut}
            className="text-sm text-foreground-600 hover:text-foreground-900 cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
          >
            <i className="ri-logout-box-line"></i>
            Wyloguj
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
          <button
            onClick={() => setTab("reservations")}
            className={`px-5 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              tab === "reservations"
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-list-check-3 mr-1.5"></i>
            Rezerwacje
          </button>
          <button
            onClick={() => setTab("pricing")}
            className={`px-5 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              tab === "pricing"
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-money-euro-circle-line mr-1.5"></i>
            Edytor Cen
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4 flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <span>{error}</span>
            <button
              onClick={() => {
                setError("");
                fetchReservations();
                fetchPricing();
              }}
              className="ml-auto text-red-700 underline cursor-pointer whitespace-nowrap"
            >
              Spróbuj ponownie
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          </div>
        ) : tab === "reservations" ? (
          /* --- Reservations Tab --- */
          <div>
            {/* Status filter */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-sm text-foreground-600">Filtruj:</span>
              {["all", "pending", "confirmed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                    filterStatus === s
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {s === "all" ? "Wszystkie" : STATUS_LABELS[s]?.label || s}
                </button>
              ))}
              <span className="text-xs text-foreground-400 ml-auto">
                {filteredReservations.length} rezerwacj{filteredReservations.length === 1 ? "a" : filteredReservations.length < 5 ? "e" : "i"}
              </span>
            </div>

            {filteredReservations.length === 0 ? (
              <div className="text-center py-20 text-foreground-500">
                <i className="ri-inbox-line text-4xl block mb-3"></i>
                <p className="text-sm">Brak rezerwacji</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 text-left">
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Data</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Klient</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Wycieczka</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Wariant</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Goście</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Łącznie</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
                      <th className="py-3 px-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReservations.map((r) => (
                      <tr key={r.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-foreground-600 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString("pl-PL")}
                        </td>
                        <td className="py-3 px-3">
                          <div className="font-medium text-foreground-950">{r.name}</div>
                          <div className="text-xs text-foreground-500">{r.email}</div>
                          <div className="text-xs text-foreground-500">{r.phone}</div>
                        </td>
                        <td className="py-3 px-3 text-foreground-800">{r.excursion_title}</td>
                        <td className="py-3 px-3 text-foreground-600">{r.variant_label}</td>
                        <td className="py-3 px-3 text-foreground-600 whitespace-nowrap">
                          {r.adults}D{r.children > 0 ? ` / ${r.children}Dz` : ""}{r.infants > 0 ? ` / ${r.infants}N` : ""}
                        </td>
                        <td className="py-3 px-3 text-foreground-950 font-medium whitespace-nowrap">
                          €{Number(r.total_price).toFixed(2)}
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              STATUS_LABELS[r.status]?.cls || "bg-background-100 text-foreground-600"
                            }`}
                          >
                            {STATUS_LABELS[r.status]?.label || r.status}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1">
                            {r.status !== "confirmed" && (
                              <button
                                onClick={() => updateReservationStatus(r.id, "confirmed")}
                                className="w-7 h-7 flex items-center justify-center rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer text-xs"
                                title="Potwierdź"
                              >
                                <i className="ri-check-line"></i>
                              </button>
                            )}
                            {r.status !== "pending" && (
                              <button
                                onClick={() => updateReservationStatus(r.id, "pending")}
                                className="w-7 h-7 flex items-center justify-center rounded bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer text-xs"
                                title="Oczekuje"
                              >
                                <i className="ri-time-line"></i>
                              </button>
                            )}
                            {r.status !== "cancelled" && (
                              <button
                                onClick={() => updateReservationStatus(r.id, "cancelled")}
                                className="w-7 h-7 flex items-center justify-center rounded bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer text-xs"
                                title="Anuluj"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            )}
                            <button
                              onClick={() => deleteReservation(r.id)}
                              className="w-7 h-7 flex items-center justify-center rounded bg-foreground-100 text-foreground-500 hover:bg-red-100 hover:text-red-700 cursor-pointer text-xs ml-0.5"
                              title="Usuń"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* --- Price Editor Tab --- */
          <div>
            <p className="text-sm text-foreground-500 mb-4">
              Edytuj ceny wycieczek. Zmiany obowiązują natychmiast na stronie.
            </p>

            <div className="space-y-4">
              {pricingData.map((p) => (
                <div
                  key={p.id}
                  className="border border-background-200/70 rounded-xl p-5"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {p.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{p.slug}</span>
                    </div>
                    {editingSlug !== p.slug ? (
                      <button
                        onClick={() => startEditing(p)}
                        className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>
                        Edytuj ceny
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={cancelEditing}
                          className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
                        >
                          Anuluj
                        </button>
                        <button
                          onClick={() => savePricing(p.slug)}
                          disabled={savingPricing}
                          className="text-sm bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 dark:text-foreground-950 px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap"
                        >
                          {savingPricing ? "Zapisywanie..." : "Zapisz"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Variants */}
                  <div className="space-y-2">
                    {(editingSlug === p.slug ? editVariants : p.variants).map(
                      (v, idx) => (
                        <div
                          key={v.id}
                          className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3"
                        >
                          <span className="text-sm text-foreground-800 flex-1">
                            {v.label}
                          </span>
                          {editingSlug === p.slug ? (
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-foreground-500">€</span>
                                <input
                                  type="number"
                                  value={v.price}
                                  onChange={(e) =>
                                    updateVariantPrice(
                                      idx,
                                      Number(e.target.value),
                                    )
                                  }
                                  className="w-20 text-sm bg-background-50 border border-background-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400 text-right"
                                />
                              </div>
                              {v.child !== undefined && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-foreground-500">Dziecko €</span>
                                  <input
                                    type="number"
                                    value={v.child}
                                    onChange={(e) =>
                                      updateVariantChildPrice(
                                        idx,
                                        Number(e.target.value),
                                      )
                                    }
                                    className="w-20 text-sm bg-background-50 border border-background-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400 text-right"
                                  />
                                </div>
                              )}
                              {v.infant !== undefined && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-foreground-500">Niemowlę €</span>
                                  <input
                                    type="number"
                                    value={v.infant}
                                    onChange={(e) =>
                                      updateVariantInfantPrice(
                                        idx,
                                        Number(e.target.value),
                                      )
                                    }
                                    className="w-20 text-sm bg-background-50 border border-background-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400 text-right"
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium text-foreground-950">
                                €{v.price}
                              </span>
                              {v.child !== undefined && (
                                <span className="text-xs text-foreground-500">
                                  Dziecko: €{v.child}
                                </span>
                              )}
                              {v.infant !== undefined && (
                                <span className="text-xs text-foreground-500">
                                  Niemowlę: €{v.infant}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>

                  {editingSlug === p.slug && pricingMsg && (
                    <div
                      className={`mt-3 text-sm rounded-lg p-2 ${
                        pricingMsg === "Prices saved!"
                          ? "bg-accent-100 text-accent-800"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {pricingMsg === "Prices saved!" ? "Ceny zapisane!" : pricingMsg}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}